import { useState } from 'react';
import Footer from '../home/components/Footer';

export default function MediaKitPage() {
  const [copied, setCopied] = useState(false);

  const boilerplateText = `The Algorithmic Consistency Initiative (algorithmicconsistency.org) is a California-registered think tank dedicated to advancing AI safety through Constitutional AI frameworks and evidence-based policy reform. Founded by Alberto Rocha, a legal and technical expert in AI governance, the Initiative develops practical solutions to the AI liability crisis, including the Constitutional AI Engineering Standard and comprehensive policy frameworks for federal and state implementation.`;

  const handleCopy = () => {
    navigator.clipboard.writeText(boilerplateText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const brandColors = [
    { name: 'Primary Teal', hex: '#14B8A6', rgb: 'RGB(20, 184, 166)' },
    { name: 'Dark Gray', hex: '#1F2937', rgb: 'RGB(31, 41, 55)' },
    { name: 'Light Teal', hex: '#5EEAD4', rgb: 'RGB(94, 234, 212)' },
    { name: 'Off White', hex: '#F9FAFB', rgb: 'RGB(249, 250, 251)' }
  ];

  const handleDownloadColors = () => {
    const colorData = brandColors.map(color => 
      `${color.name}\nHEX: ${color.hex}\n${color.rgb}\n`
    ).join('\n');
    
    const blob = new Blob([`The Algorithmic Consistency Initiative - Brand Colors\n\n${colorData}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'algorithmic-consistency-brand-colors.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white">
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Media Kit</h1>
          <p className="text-xl text-gray-600 mb-8">
            Resources for journalists, reporters, and media professionals covering AI safety, Constitutional AI, and policy reform.
          </p>
          
          {/* Quick Access */}
          <div className="flex flex-wrap gap-4">
            <a href="#about" className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer">
              About Us
            </a>
            <a href="#founder" className="px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
              Founder Bio
            </a>
            <a href="#logos" className="px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
              Download Logos
            </a>
            <a href="#contact" className="px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
              Media Contact
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About The Algorithmic Consistency Initiative</h2>
          
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-8">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Boilerplate Text</h3>
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {copied ? 'Copied!' : 'Copy Text'}
              </button>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {boilerplateText}
            </p>
          </div>

          {/* Quick Facts */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Facts</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-semibold text-teal-600 mb-1">Organization Type</p>
                <p className="text-gray-900">California Registered Think Tank</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-teal-600 mb-1">Website</p>
                <p className="text-gray-900">algorithmicconsistency.org</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-teal-600 mb-1">Focus Areas</p>
                <p className="text-gray-900">AI Safety, Constitutional AI, Policy Reform</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-teal-600 mb-1">Founder</p>
                <p className="text-gray-900">Alberto Rocha</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Bio */}
      <section id="founder" className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Founder &amp; Leadership</h2>
          
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-48 h-48 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/7c12457f0c3ed53f27ff6ce928c65419.png"
                  alt="Alberto Rocha - Founder"
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Alberto Rocha</h3>
                <p className="text-teal-600 font-semibold mb-4">Founder &amp; Principal Researcher</p>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  Alberto Rocha is a legal and technical expert specializing in AI governance, Constitutional AI frameworks, and evidence-based policy reform. With deep expertise in both the technical and legal dimensions of AI safety, Alberto founded The Algorithmic Consistency Initiative to bridge the gap between AI innovation and accountability.
                </p>
                
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Expertise Areas:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Constitutional AI Engineering</li>
                    <li>AI Liability and Governance</li>
                    <li>Federal and State AI Policy</li>
                    <li>NIST AI Risk Management Framework</li>
                    <li>AI Safety Standards and Compliance</li>
                  </ul>
                </div>
                
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Available for:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">Interviews</span>
                    <span className="px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">Expert Commentary</span>
                    <span className="px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">Speaking Engagements</span>
                    <span className="px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">Policy Briefings</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Publications */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Publications</h2>
          
          <div className="grid gap-6">
            {[
              {
                title: 'Constitutional AI Engineering Standard',
                description: 'Technical framework for implementing Constitutional AI in production systems',
                link: '/cai-engineering-standard'
              },
              {
                title: 'AI Liability Crisis White Paper',
                description: 'Comprehensive analysis of the current AI liability landscape and proposed solutions',
                link: '/ai-liability'
              },
              {
                title: 'State of AI Safety 2025',
                description: 'Annual report on AI safety progress, challenges, and policy recommendations',
                link: '/state-of-ai-2025'
              },
              {
                title: 'Constitutional AI Framework',
                description: 'Foundational principles and implementation guide for Constitutional AI',
                link: '/constitutional-ai'
              }
            ].map((pub, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pub.title}</h3>
                <p className="text-gray-600 mb-4">{pub.description}</p>
                <a
                  href={pub.link}
                  className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
                >
                  Read Publication
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talking Points */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Talking Points</h2>
          
          <div className="grid gap-6">
            {[
              {
                topic: 'AI Liability Crisis',
                point: 'Current AI systems lack constitutional constraints, creating unprecedented liability risks for organizations and society.'
              },
              {
                topic: 'Constitutional AI Solution',
                point: 'Constitutional AI provides a practical framework for embedding ethical constraints and accountability into AI systems from the ground up.'
              },
              {
                topic: 'Policy Reform',
                point: 'Federal and state governments need evidence-based AI governance frameworks that balance innovation with public safety.'
              },
              {
                topic: 'Engineering Standards',
                point: 'The CAI Engineering Standard offers concrete technical specifications for building safer, more accountable AI systems.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold text-teal-600 mb-2">{item.topic}</h3>
                <p className="text-gray-700 leading-relaxed">{item.point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Assets */}
      <section id="logos" className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Brand Assets</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <div className="w-full h-48 bg-gray-50 rounded-lg flex items-center justify-center mb-4 p-4">
                <img 
                  src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/27c8cb1f9788b3e16d9c3346da5dbbef.png" 
                  alt="The Algorithmic Consistency Initiative Logo"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <a 
                href="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/27c8cb1f9788b3e16d9c3346da5dbbef.png"
                download="algorithmic-consistency-logo.png"
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center"
              >
                <i className="ri-download-line mr-2"></i>
                Download PNG
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Color Palette</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {brandColors.map((color, index) => (
                  <div key={index} className="text-center">
                    <div 
                      className="w-full h-16 rounded-lg mb-2 border border-gray-200"
                      style={{ backgroundColor: color.hex }}
                    ></div>
                    <p className="text-xs font-semibold text-gray-900">{color.name}</p>
                    <p className="text-xs text-gray-600">{color.hex}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={handleDownloadColors}
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center"
              >
                <i className="ri-download-line mr-2"></i>
                Download Colors
              </button>
            </div>
          </div>
          
          <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Brand Guidelines</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                <span>Always refer to the organization as "The Algorithmic Consistency Initiative"</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                <span>Maintain clear space around the logo equal to the height of the letter "A"</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                <span>Primary brand color: Teal (#14B8A6)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                <span>Do not alter, rotate, or distort the logo</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Media Contact */}
      <section id="contact" className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Media Contact</h2>
          
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">For Media Inquiries</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-mail-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <a href="mailto:media@algorithmicconsistency.org" className="text-gray-900 hover:text-teal-600 cursor-pointer">
                        media@algorithmicconsistency.org
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-time-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <p className="text-sm text-gray-600">Response Time</p>
                      <p className="text-gray-900">Within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Interview Requests</h3>
                <p className="text-gray-700 mb-4">
                  Alberto Rocha is available for interviews, expert commentary, and speaking engagements on AI safety, Constitutional AI, and policy reform.
                </p>
                <p className="text-sm text-gray-600">
                  Please include your deadline, publication name, and specific topics in your inquiry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Additional Resources</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <a href="/publications" className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <i className="ri-file-text-line text-3xl text-teal-600 mb-3"></i>
              <h3 className="text-lg font-bold text-gray-900 mb-2">All Publications</h3>
              <p className="text-gray-600">Browse our complete library of white papers and research</p>
            </a>
            
            <a href="/infographics" className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <i className="ri-image-line text-3xl text-teal-600 mb-3"></i>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Infographics</h3>
              <p className="text-gray-600">Visual summaries and shareable graphics</p>
            </a>
            
            <a href="/rss.xml" className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <i className="ri-rss-line text-3xl text-teal-600 mb-3"></i>
              <h3 className="text-lg font-bold text-gray-900 mb-2">RSS Feed</h3>
              <p className="text-gray-600">Subscribe for automatic updates on new publications</p>
            </a>
            
            <a href="/faq" className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <i className="ri-question-line text-3xl text-teal-600 mb-3"></i>
              <h3 className="text-lg font-bold text-gray-900 mb-2">FAQ</h3>
              <p className="text-gray-600">Frequently asked questions about our work</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}